import { memo, useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { TodoListSectionItemLinkContent } from "./link-content";

import type { ComponentProps } from "react";

type Props = {
	/**
	 * driver.js のターゲット用 ID
	 */
	id?: string;

	/**
	 * 遷移可能な条件
	 */
	transitionCondition: boolean;

	/**
	 * 遷移できない場合に表示するメッセージ
	 */
	alertMessageWhenNotTransition?: string;

	/**
	 * 遷移するページのパス
	 */
	pagePath: string;
} & ComponentProps<typeof TodoListSectionItemLinkContent>;

export const TodoListSectionItem = memo(
	({
		id,
		transitionCondition,
		alertMessageWhenNotTransition = "先に他の項目を設定してください",
		pagePath,
		...props
	}: Props) => {
		const navigate = useNavigate();

		const handleClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
			(e) => {
				if (!transitionCondition) return;

				e.preventDefault();
				navigate(pagePath);
			},
			[transitionCondition, navigate, pagePath],
		);

		const to = useMemo(() => (transitionCondition ? pagePath : {}), [transitionCondition, pagePath]);

		const [isShowAlertMessage, setIsShowAlertMessage] = useState(false);

		const handleMouseEnter = useCallback(() => {
			if (!transitionCondition) setIsShowAlertMessage(true);
		}, [transitionCondition]);

		const handleMouseLeave = useCallback(() => {
			if (!transitionCondition) setIsShowAlertMessage(false);
		}, [transitionCondition]);

		if (!transitionCondition) {
			return (
				<div
					id={id}
					className="relative block cursor-not-allowed select-none rounded p-2"
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					<TodoListSectionItemLinkContent {...props} />

					{isShowAlertMessage && (
						<div className="absolute left-0 top-0 inline rounded border border-zinc-200 bg-white p-3 shadow">
							{alertMessageWhenNotTransition}
						</div>
					)}
				</div>
			);
		}

		return (
			<Link
				id={id}
				to={to}
				viewTransition
				className={"block rounded p-2 hover:bg-zinc-100"}
				onClick={handleClick}
			>
				<TodoListSectionItemLinkContent {...props} />
			</Link>
		);
	},
);

TodoListSectionItem.displayName = "TodoListSectionItem";
